/**
 * F5-T1a — catálogo de Admin 08 Configuración.
 *
 * Una pantalla, cinco grupos. Las 62 claves vivas de staging (medidas
 * 2026-08-27) viven acá UNA vez. daily_screening_target se pliega en la
 * meta diaria única (F-5): no es una segunda fila. No se escribe ningún
 * valor desde este archivo — F-5 default 15 NO se aplica si el vivo no
 * es 15 (medido: agent_daily_target=25 y daily_screening_target=25).
 *
 * T1b (vigencia versionada / mig 078+) no vive acá.
 */

export type ConfigGroupId =
  | 'operacion'
  | 'metas'
  | 'nucleo'
  | 'navegacion'
  | 'congeladas';

export type FieldKind =
  | { kind: 'int'; unit: 'minutes' | 'seconds' | 'attempts' | 'days' | 'years' | 'points' | 'hours' | 'screenings' | 'uploads' | 'units' | 'followups' | 'dayOfMonth' | 'weight' }
  | { kind: 'string' }
  | { kind: 'url' }
  | { kind: 'bool' }
  | { kind: 'userId' }
  | { kind: 'roleArray' }
  | { kind: 'intArray' }
  | { kind: 'stringArray' }
  | { kind: 'weeklyRhythm' }
  | { kind: 'approvalPct' }
  | { kind: 'monthlyLadder' }
  | { kind: 'timeBands' };

export interface CatalogKey {
  key: string;
  group: ConfigGroupId;
  field: FieldKind;
  /** Payroll-tracked (HISTORY_TRACKED_KEYS) — keep existing N-Q6 modal. */
  payroll?: boolean;
  /** Touches navigation billing units / timer cap. Tag only; T1b vigencia stays out. */
  billing?: boolean;
  frozen?: boolean;
  /** Folded into UNIQUE_DAILY_KEY — not its own row. */
  hideRow?: boolean;
  duplicateOf?: string;
}

export interface FrozenFact {
  id: string;
  value: string;
}

export const UNIQUE_DAILY_KEY = 'agent_daily_target';
export const FOLDED_DAILY_KEY = 'daily_screening_target';

export const GROUP_ORDER: ConfigGroupId[] = [
  'operacion',
  'metas',
  'nucleo',
  'navegacion',
  'congeladas',
];

export const CATALOG: CatalogKey[] = [
  // —— Operación y seguridad ——
  { key: 'session_timeout_min', group: 'operacion', field: { kind: 'int', unit: 'minutes' } },
  { key: 'idle_threshold_sec', group: 'operacion', field: { kind: 'int', unit: 'seconds' } },
  { key: 'lockout_after_failed_logins', group: 'operacion', field: { kind: 'int', unit: 'attempts' } },
  { key: 'lockout_duration_min', group: 'operacion', field: { kind: 'int', unit: 'minutes' } },
  { key: 'heartbeat_interval_sec', group: 'operacion', field: { kind: 'int', unit: 'seconds' } },
  { key: 'max_call_attempts', group: 'operacion', field: { kind: 'int', unit: 'attempts' } },
  { key: 'min_call_duration_sec', group: 'operacion', field: { kind: 'int', unit: 'seconds' } },
  { key: 'rescreening_blackout_days', group: 'operacion', field: { kind: 'int', unit: 'days' } },
  { key: 'phi_retention_years', group: 'operacion', field: { kind: 'int', unit: 'years' } },
  { key: 'audit_retention_years', group: 'operacion', field: { kind: 'int', unit: 'years' } },
  { key: 'safety_score_alert_threshold', group: 'operacion', field: { kind: 'int', unit: 'points' } },
  { key: 'eligible_zip_prefix', group: 'operacion', field: { kind: 'string' } },
  { key: 'privacy_officer_user_id', group: 'operacion', field: { kind: 'userId' } },
  { key: 'config_cache_ttl_sec', group: 'operacion', field: { kind: 'int', unit: 'seconds' } },
  { key: 'uploader_sla_avg_time_to_upload_hours', group: 'operacion', field: { kind: 'int', unit: 'hours' } },
  { key: 'uploader_sla_oldest_pending_hours', group: 'operacion', field: { kind: 'int', unit: 'hours' } },

  // —— Metas y niveles ——
  { key: 'agent_daily_target', group: 'metas', field: { kind: 'int', unit: 'screenings' }, payroll: true },
  {
    key: 'daily_screening_target',
    group: 'metas',
    field: { kind: 'int', unit: 'screenings' },
    hideRow: true,
    duplicateOf: UNIQUE_DAILY_KEY,
  },
  { key: 'agent_tier_thresholds', group: 'metas', field: { kind: 'monthlyLadder' }, payroll: true },
  { key: 'uploader_daily_target', group: 'metas', field: { kind: 'int', unit: 'uploads' }, payroll: true },
  { key: 'uploader_tier_thresholds', group: 'metas', field: { kind: 'monthlyLadder' }, payroll: true },
  { key: 'weekly_thresholds', group: 'metas', field: { kind: 'weeklyRhythm' } },
  { key: 'approval_thresholds', group: 'metas', field: { kind: 'approvalPct' } },
  { key: 'uploader_weekly_target', group: 'metas', field: { kind: 'int', unit: 'uploads' } },
  { key: 'metrics_cycle_start_day', group: 'metas', field: { kind: 'int', unit: 'dayOfMonth' } , payroll: true },

  // —— Núcleo ——
  { key: 'nav_findhelp_base_url', group: 'nucleo', field: { kind: 'url' } },
  { key: 'findhelp_sla_digest_recipients', group: 'nucleo', field: { kind: 'intArray' } },

  // —— Navegación (35; FindHelp URL vive en Núcleo) ——
  { key: 'nav_assignment_weight_open_episode', group: 'navegacion', field: { kind: 'int', unit: 'weight' } },
  { key: 'nav_assignment_weight_overdue_followup', group: 'navegacion', field: { kind: 'int', unit: 'weight' } },
  { key: 'nav_assignment_weight_upcoming_appointment', group: 'navegacion', field: { kind: 'int', unit: 'weight' } },
  { key: 'nav_assignment_appointment_horizon_days', group: 'navegacion', field: { kind: 'int', unit: 'days' } },
  { key: 'nav_ea_validity_days', group: 'navegacion', field: { kind: 'int', unit: 'days' } },
  { key: 'nav_timer_idle_minutes', group: 'navegacion', field: { kind: 'int', unit: 'minutes' } },
  { key: 'nav_timer_idle_espejo_minutes', group: 'navegacion', field: { kind: 'int', unit: 'minutes' } },
  { key: 'nav_timer_max_minutes', group: 'navegacion', field: { kind: 'int', unit: 'minutes' }, billing: true, payroll: true },
  { key: 'nav_timer_delta_threshold_minutes', group: 'navegacion', field: { kind: 'int', unit: 'minutes' }, billing: true, payroll: true },
  { key: 'nav_no_show_grace_minutes', group: 'navegacion', field: { kind: 'int', unit: 'minutes' } },
  { key: 'nav_general_followup_days', group: 'navegacion', field: { kind: 'int', unit: 'days' } },
  { key: 'nav_referral_no_response_business_days', group: 'navegacion', field: { kind: 'int', unit: 'days' } },
  { key: 'nav_auto_suggest_enabled', group: 'navegacion', field: { kind: 'bool' } },
  { key: 'nav_followup_continue_until_terminal', group: 'navegacion', field: { kind: 'bool' } },
  { key: 'nav_pool_enabled', group: 'navegacion', field: { kind: 'bool' } },
  { key: 'nav_dual_role_enabled', group: 'navegacion', field: { kind: 'bool' } },
  { key: 'nav_unit_minutes', group: 'navegacion', field: { kind: 'int', unit: 'minutes' }, billing: true, payroll: true },
  { key: 'nav_min_billable_minutes', group: 'navegacion', field: { kind: 'int', unit: 'minutes' }, billing: true, payroll: true },
  { key: 'nav_l1_max_units_event', group: 'navegacion', field: { kind: 'int', unit: 'units' }, billing: true, payroll: true },
  { key: 'nav_l1_max_units_year', group: 'navegacion', field: { kind: 'int', unit: 'units' }, billing: true, payroll: true },
  { key: 'nav_l2_max_units_month', group: 'navegacion', field: { kind: 'int', unit: 'units' }, billing: true, payroll: true },
  { key: 'nav_outreach_attempts_required', group: 'navegacion', field: { kind: 'int', unit: 'attempts' } },
  { key: 'nav_outreach_window_days', group: 'navegacion', field: { kind: 'int', unit: 'days' } },
  { key: 'nav_outreach_max_attempts', group: 'navegacion', field: { kind: 'int', unit: 'attempts' } },
  { key: 'nav_outreach_time_bands', group: 'navegacion', field: { kind: 'timeBands' } },
  { key: 'nav_insurance_carriers', group: 'navegacion', field: { kind: 'stringArray' } },
  { key: 'nav_preauth_required_services', group: 'navegacion', field: { kind: 'stringArray' } },
  { key: 'nav_referral_stale_days', group: 'navegacion', field: { kind: 'int', unit: 'days' } },
  { key: 'nav_untouched_case_days', group: 'navegacion', field: { kind: 'int', unit: 'days' } },
  { key: 'nav_weekly_contact_goal', group: 'navegacion', field: { kind: 'int', unit: 'followups' } },
  { key: 'nav_weekly_received_goal', group: 'navegacion', field: { kind: 'int', unit: 'followups' } },
  { key: 'nav_weekly_completed_goal', group: 'navegacion', field: { kind: 'int', unit: 'followups' } },
  { key: 'nav_day_window_start', group: 'navegacion', field: { kind: 'int', unit: 'hours' } },
  { key: 'nav_day_window_end', group: 'navegacion', field: { kind: 'int', unit: 'hours' } },
  { key: 'nav_day_shift_end', group: 'navegacion', field: { kind: 'int', unit: 'hours' } },

  // —— Congeladas: la ÚNICA clave readonly del API (READONLY_KEYS) ——
  { key: 'mfa_required_roles', group: 'congeladas', field: { kind: 'roleArray' }, frozen: true },
];

/**
 * Hechos congelados descubiertos en código, NO inventados como filas de
 * system_config. Se muestran para que nadie los busque en la base.
 *   · timezone: America/New_York está hardcodeada (no hay clave)
 *   · cifrado: pgcrypto / app_encrypt (no hay clave)
 * El formato CIN del mockup (2 letras + 7 dígitos) NO se lista: el código
 * vigente acepta 6–30 caracteres, no ese regex.
 */
export const FROZEN_FACTS: FrozenFact[] = [
  { id: 'tz_america_new_york', value: 'America/New_York' },
  { id: 'phi_cipher', value: 'pgcrypto · app_encrypt' },
];

export const catalogByKey = new Map(CATALOG.map((c) => [c.key, c]));

export function keysInGroup(group: ConfigGroupId): CatalogKey[] {
  return CATALOG.filter((c) => c.group === group && !c.hideRow);
}

export function visibleKeyCount(group: ConfigGroupId): number {
  if (group === 'congeladas') return keysInGroup(group).length + FROZEN_FACTS.length;
  return keysInGroup(group).length;
}
